import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { CalendarBlank } from "@phosphor-icons/react";

const CYCLE_LABEL = { yearly: "Yearly (credited upfront on 1 Jan)", half_yearly: "Half-yearly (1 Jan, 1 Jul)", quarterly: "Quarterly (start of each quarter)", monthly: "Monthly (start of each month)" };

export default function LeaveRulesPanel({ params }) {
  const [rules, setRules] = useState(null);
  const [preview, setPreview] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/leave-rules", { params }).then(({ data }) => { setRules(data.rules); setPreview(data.accrued_preview || {}); }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params.tenant_id]);

  const set = (lt, k, v) => setRules({ ...rules, [lt]: { ...rules[lt], [k]: v } });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/leave-rules", { rules, ...params }); setRules(data.rules); setPreview(data.accrued_preview || {}); toast.success("Leave definitions saved — balances recalculated"); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!rules) return <div className="text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="leave-rules-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-xl font-bold flex items-center gap-2"><CalendarBlank size={20} /> Leave definitions</div>
          <div className="text-xs text-zinc-500 mt-1">Define CL, SL and Annual leave quotas and how they are credited. Credits are applied automatically on the cycle you choose (pro-rated for employees who joined this year). Unused balance from the previous year rolls over automatically up to the carry-forward cap.</div>
        </div>
        <Button onClick={save} disabled={busy} className="btn-zenith rounded-sm" data-testid="leave-rules-save">{busy ? "Saving…" : "Save definitions"}</Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Object.entries(rules).map(([lt, r]) => (
          <div key={lt} className="border border-zinc-200 bg-white rounded-sm p-5 space-y-3" data-testid={`leave-rule-${lt}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{lt}</div>
              <div className="text-[11px] text-emerald-700 font-mono-plex" data-testid={`leave-rule-accrued-${lt}`}>Credited so far: {preview[lt] ?? "—"} d</div>
            </div>
            <div><Label className="text-xs">Display name</Label><Input value={r.label || ""} onChange={(e) => set(lt, "label", e.target.value)} className="rounded-sm mt-1" data-testid={`leave-rule-label-${lt}`} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Code</Label><Input value={r.code || ""} onChange={(e) => set(lt, "code", e.target.value.toUpperCase().slice(0, 4))} className="rounded-sm mt-1 font-mono-plex" data-testid={`leave-rule-code-${lt}`} /></div>
              <div><Label className="text-xs">Annual quota (days)</Label><Input type="number" min={0} value={r.annual_quota} onChange={(e) => set(lt, "annual_quota", e.target.value)} className="rounded-sm mt-1 font-mono-plex" data-testid={`leave-rule-quota-${lt}`} /></div>
            </div>
            <div>
              <Label className="text-xs">Credit cycle</Label>
              <Select value={r.credit_cycle} onValueChange={(v) => set(lt, "credit_cycle", v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid={`leave-rule-cycle-${lt}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CYCLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Carry-forward cap (0 = lapses)</Label><Input type="number" min={0} value={r.carry_forward_max} onChange={(e) => set(lt, "carry_forward_max", e.target.value)} className="rounded-sm mt-1 font-mono-plex" data-testid={`leave-rule-cf-${lt}`} /></div>
              <div><Label className="text-xs">Max per request (0 = any)</Label><Input type="number" min={0} value={r.max_per_request} onChange={(e) => set(lt, "max_per_request", e.target.value)} className="rounded-sm mt-1 font-mono-plex" data-testid={`leave-rule-max-${lt}`} /></div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <Label className="text-xs">Needs manager approval</Label>
              <Switch checked={!!r.requires_approval} onCheckedChange={(v) => set(lt, "requires_approval", v)} data-testid={`leave-rule-approval-${lt}`} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Encashable at exit</Label>
              <Switch checked={!!r.encashable} onCheckedChange={(v) => set(lt, "encashable", v)} data-testid={`leave-rule-encash-${lt}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
